import React, { useState, useEffect } from "react";
import { formatDateInput, parseTrDate, isoToTr } from "@/lib/date";

/**
 * Text input for Turkish dates (DD.MM.YYYY) with auto-inserted dots as the user types.
 * The parent component receives the value as ISO YYYY-MM-DD via `onChange(isoString)`.
 * Displays "gg.aa.yyyy" placeholder. When the parent sets an ISO value, it is
 * shown as DD.MM.YYYY.
 *
 * Props:
 *   - value: ISO YYYY-MM-DD (or empty)
 *   - onChange: (isoOrEmpty: string) => void
 *   - disabled, placeholder, className, data-testid, ...
 */
export default function TrDateInput({ value, onChange, disabled, placeholder = "gg.aa.yyyy", className = "", ...rest }) {
  const [display, setDisplay] = useState(isoToTr(value));

  // Sync when parent-provided ISO value changes externally
  useEffect(() => { setDisplay(isoToTr(value)); }, [value]);

  const handleChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setDisplay(formatted);
    // Only fire ISO onChange when we have a complete DD.MM.YYYY
    if (formatted.length === 10) {
      const iso = parseTrDate(formatted);
      if (iso) onChange(iso);
      else onChange(""); // invalid
    } else if (formatted === "") {
      onChange("");
    }
  };
  const handleBlur = () => {
    // On blur, if partial → clear ISO to avoid stale value
    if (display && display.length < 10) onChange("");
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      maxLength={10}
      className={className}
      {...rest}
    />
  );
}
